import React from 'react'

import type { HolidayImage as HolidayImageData } from '@/src/types/holidays'

/**
 * Renders a CMS image, choosing the separately-cropped mobile file when the document carries one.
 *
 * The crop is picked here from the server's device detection rather than by a `<picture>` with a
 * media query. `<picture>` is the textbook answer for art direction, but React could not hydrate it
 * here — bisecting proved every card's mismatch disappeared the moment the `<picture>` wrapper went
 * away. Choosing server-side also matches how the rest of this app already works: it renders
 * entirely separate desktop and mobile trees from the same signal, so the crop and the layout can
 * never disagree about which device they are drawing for.
 *
 * Trade-off: a desktop window dragged below 992px keeps the desktop crop until it reloads — the
 * same trade-off every other device-split component in this repo already makes.
 *
 * The fill is a plain `style` attribute rather than `sx` so the image introduces no emotion class.
 * No theme value is involved, so nothing here escapes the palette.
 *
 * Deliberately not `next/image`: these are remote CMS URLs, and next/image would need the host
 * added to `next.config.js` — which this repo's working agreement puts off-limits without asking.
 */
interface HolidayImageProps {
    image: HolidayImageData
    /** Prefers the document's mobile crop when one exists. */
    isMobile?: boolean
    /** Lazy by default; pass false for the above-the-fold hero. */
    lazy?: boolean
}

const FILL_STYLE: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover'
}

const HolidayImage: React.FC<HolidayImageProps> = ({ image, isMobile = false, lazy = true }) => {
    const src = isMobile && image.mobileUrl ? image.mobileUrl : image.url

    return <img src={src} alt={image.alt} loading={lazy ? 'lazy' : undefined} style={FILL_STYLE} />
}

export { HolidayImage }
export type { HolidayImageProps }
export default HolidayImage
